/* eslint-disable no-restricted-globals */
/* eslint-disable @typescript-eslint/dot-notation */
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFilterValuesWithExtraValues,
  getLeadListingPageSize,
  getLeadsActiveTab,
  validateNumberForQueryParams,
} from '../../helpers/helper';
import {
  DEPARTMENT_DATA,
  INDUSTRY_DATA,
  LOCATION_DATA,
} from '../../helpers/filter-data';
import {
  fetchAISearchResult,
  getLeads,
  getSavedLeads,
} from '../../helpers/leads.api';
import { LeadFinderTabKey, LeadRevealType, SavedTabFilters } from '../../type';
import { RootState } from '../../../../store/root-reducer';
import toaster, { Theme } from '../../../../shared/toaster';
import { dateToString } from '../../../../shared/functions/date';
import { ExceptionTypes } from '../../../../shared/types';
import {
  setAiSearchPopupInputValue,
  setAiSearchPopupSubmitted,
  setIsAiSearchInProgress,
} from '../../lead-slice';
import { redirectWithoutRefresh } from '../../../../shared/utils';
import { StorageUtil } from '../../../../shared/utils/storage';

export const useLeadFinder = (
  tabKey: string,
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>,
  setEmailRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >,
  setEmailPhoneRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >,
) => {
  const history = useHistory();
  const location = useLocation();

  // ** Redux
  const dispatch = useDispatch();
  const userEmail = useSelector((state: RootState) => state?.home?.email);
  const {
    aiSeachPopupSubmitted,
    aiSearchInputValue,
    isAiSearchInProgress,
  } = useSelector((state: RootState) => state?.leads);

  const queryParams = new URLSearchParams(location?.search);

  const [isLoading, setIsLoading] = useState(false);
  const [urlState, setUrlState] = useState<any>();
  const [apiParam, setApiParam] = useState<any>({
    start: validateNumberForQueryParams(queryParams.get('start')),
    take: validateNumberForQueryParams(
      queryParams.get('take'),
      getLeadListingPageSize(),
    ),
  });
  const [savedApiParam, setSavedApiParam] = useState<any>({
    start: validateNumberForQueryParams(queryParams.get('start')),
    take: validateNumberForQueryParams(
      queryParams.get('take'),
      getLeadListingPageSize(),
    ),
  });
  const [leadsData, setLeadsData] = useState({
    profiles: [],
    pagination: null,
  });

  const handleGeneratePayload = () => {
    const queryParamsUpdated = new URLSearchParams(window.location?.search);
    const paramArray = Array.from(queryParamsUpdated.entries());
    const tempParam = {};
    const savedFilters: SavedTabFilters = {
      createdDate: [],
      search: '',
      tags: [],
    };

    paramArray.forEach(([key, value]) => {
      switch (key) {
        case 'keyword':
          if (!value) {
            tempParam['start'] = 1;
            tempParam['take'] = getLeadListingPageSize();
          } else {
            tempParam[key] = queryParamsUpdated.getAll(key);
            savedFilters.search = value;
          }
          break;
        case 'tags':
          if (!value) {
            tempParam['start'] = 1;
            tempParam['take'] = getLeadListingPageSize();
          } else {
            tempParam[key] = value;
            savedFilters.tags = value
              .split(',')
              .map((tag) => Number(tag.trim()));
          }
          break;
        case 'createdDate':
          if (!value) {
            tempParam['start'] = 1;
            tempParam['take'] = getLeadListingPageSize();
          } else {
            const [formattedStartDate, formattedEndDate] = value.split('-');
            const startDate = new Date(formattedStartDate);
            const endDate = new Date(formattedEndDate);
            endDate.setHours(23, 59, 59, 999); // Set to end of the day
            const dateObjects = [
              encodeURIComponent(dateToString(startDate)),
              encodeURIComponent(dateToString(endDate)),
            ];
            tempParam[key] = dateObjects.join(',');
            savedFilters.createdDate = [startDate, endDate];
          }
          break;
        case 'name':
        case 'jobChangeRange':
        case 'yearsExperience':
        case 'companySize':
        case 'companyRevenue':
          tempParam[key] = value;
          break;
        case 'employer':
        case 'excludeEmployer':
        case 'skills':
        case 'excludeSkills':
        case 'jobTitle':
        case 'excludeJobTitle':
        case 'managementLevels':
        case 'excludeManagementLevels':
        case 'sicCode':
        case 'excludeSicCode':
        case 'naicsCode':
        case 'excludeNaicsCode':
        case 'contactMethod':
        case 'major':
        case 'excludeMajor':
        case 'school':
        case 'excludeSchool':
        case 'degree':
        case 'excludeDegree':
        case 'link':
        case 'contactInfo':
          tempParam[key] = queryParamsUpdated.getAll(key); // Convert to array
          break;
        case 'department':
        case 'excludeDepartment':
          // Use getAll to handle multiple values for 'department'
          tempParam[key] = getFilterValuesWithExtraValues(
            queryParamsUpdated.getAll(key),
            DEPARTMENT_DATA,
          );
          break;
        case 'location':
        case 'excludeLocation':
          {
            // Use getAll to handle multiple values for 'location'
            const locationPayload = [];
            getFilterValuesWithExtraValues(
              queryParamsUpdated.getAll(key),
              LOCATION_DATA,
            ).forEach((item) => {
              if (
                LOCATION_DATA.some(
                  (data) => data.key === item.substring(1, 3).toUpperCase(),
                )
              ) {
                locationPayload.push(item.replace(/"/g, ''));
              } else {
                locationPayload.push(item);
              }
            });

            tempParam[key] = getFilterValuesWithExtraValues(
              locationPayload,
              LOCATION_DATA,
              true,
            );
          }
          break;
        case 'industry':
        case 'excludeIndustry':
          // Use getAll to handle multiple values for 'location'
          tempParam[key] = getFilterValuesWithExtraValues(
            queryParamsUpdated.getAll(key),
            INDUSTRY_DATA,
          );
          break;
        default:
      }
    });

    if (tabKey === LeadFinderTabKey.SAVED_TAB) {
      if (!tempParam['start']) {
        tempParam['start'] = validateNumberForQueryParams(
          queryParams.get('start'),
        );
      }
      if (!tempParam['take']) {
        tempParam['take'] = validateNumberForQueryParams(
          queryParams.get('take'),
          getLeadListingPageSize(),
        );
      }
      setSavedFilters(savedFilters);
    }

    return tempParam;
  };

  const handleGenerateAPIParam = (key: string) => {
    let payload: any = handleGeneratePayload();
    payload = {
      start: validateNumberForQueryParams(queryParams.get('start')),
      take: validateNumberForQueryParams(
        queryParams.get('take'),
        getLeadListingPageSize(),
      ),
      ...payload,
    };
    if (key === LeadFinderTabKey.PEOPLE_TAB) {
      setApiParam(() => payload);
    } else {
      setSavedApiParam(() => payload);
    }
    return payload;
  };

  const handleRecentSearch = () => {
    const payload: any = handleGeneratePayload();
    const meaningfulParams = Object.keys(payload).filter(
      (key) => key !== 'start' && key !== 'take',
    );

    if (tabKey !== LeadFinderTabKey.PEOPLE_TAB) {
      return;
    }

    if (meaningfulParams.length > 0) {
      if (!userEmail) {
        return;
      }

      const storedUrlsKey = 'storedUrls';
      const storedUrls = JSON.parse(StorageUtil.getItem(storedUrlsKey)) || {};

      if (!storedUrls[userEmail]) {
        storedUrls[userEmail] = [];
      }

      const currentUrlWithParams =
        window.location.pathname + window.location.search;
      const timestamp = new Date().toISOString();

      const existingUrlIndex = storedUrls[userEmail].findIndex(
        (urlData) =>
          urlData?.url?.toLowerCase() === currentUrlWithParams?.toLowerCase(),
      );

      const maxUrls = 4;

      if (existingUrlIndex !== -1) {
        const updatedEntry = {
          url: currentUrlWithParams,
          timestamp,
          payload,
        };

        storedUrls[userEmail].unshift(
          storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
        );
        storedUrls[userEmail][0] = updatedEntry;

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      } else {
        const urlData = {
          url: currentUrlWithParams,
          timestamp,
          payload,
        };

        storedUrls[userEmail].unshift(urlData);

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      }

      StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));

      const paramArray = Array.from(queryParams.entries());

      // eslint-disable-next-line  @typescript-eslint/no-unused-vars
      paramArray.forEach(([key]) => {
        queryParams.delete(key);
      });

      history.push({
        search: queryParams.toString(),
        hash: getLeadsActiveTab(location),
      });
    }
  };

  const handleRouteChange = () => {
    const payload: any = handleGeneratePayload();
    const meaningfulParams = Object.keys(payload).filter(
      (key) => key !== 'start' && key !== 'take',
    );

    if (tabKey !== LeadFinderTabKey.PEOPLE_TAB) {
      return;
    }

    if (meaningfulParams.length > 0) {
      if (!userEmail) {
        return;
      }

      const storedUrlsKey = 'storedUrls';
      const storedUrls = JSON.parse(StorageUtil.getItem(storedUrlsKey)) || {};

      if (!storedUrls[userEmail]) {
        storedUrls[userEmail] = [];
      }

      const currentUrlWithParams = location.pathname + location.search;
      const timestamp = new Date().toISOString();

      const existingUrlIndex = storedUrls[userEmail].findIndex(
        (urlData) =>
          urlData?.url?.toLowerCase() === currentUrlWithParams?.toLowerCase(),
      );

      const maxUrls = 4;

      if (existingUrlIndex !== -1) {
        const updatedEntry = {
          url: currentUrlWithParams,
          timestamp,
          payload,
        };

        storedUrls[userEmail].unshift(
          storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
        );
        storedUrls[userEmail][0] = updatedEntry;

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      } else {
        const urlData = {
          url: currentUrlWithParams,
          timestamp,
          payload,
        };

        storedUrls[userEmail].unshift(urlData);

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      }

      StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
    }
  };

  const handleAppendLead = (lead) => {
    const indexOfProfileToReplace = leadsData?.profiles?.findIndex(
      (profile) => profile?.id === lead?.id,
    );

    if (indexOfProfileToReplace !== -1) {
      setLeadsData((prevState) => ({
        ...prevState,
        profiles: (prevState.profiles || []).map((profile) => {
          const isCurrentLead = profile?.id === lead?.id;
          const reReveal = lead?.reReveal;
          const { tags, teaser } = profile;
          return isCurrentLead
            ? { ...lead, tags, ...(reReveal ? { teaser } : {}) }
            : profile;
        }),
      }));
    }
  };

  const updateUrlWithAiSearchParams = useCallback((filtersPayload: any) => {
    const aiSearchFilters = { ...filtersPayload };
    const currentUrl = new URL(window?.location?.href);

    if (Object.keys(aiSearchFilters)?.length) {
      const filterEntries = Object.entries(aiSearchFilters);
      filterEntries.forEach(([filterKey, filterValue]) => {
        // Delete any existing parameters with this key first
        currentUrl.searchParams.delete(filterKey);

        if (filterKey && filterValue) {
          if (Array.isArray(filterValue)) {
            filterValue.forEach((value) => {
              // Use append instead of set to allow multiple values
              currentUrl.searchParams.append(filterKey, value as string);
            });
          } else {
            currentUrl.searchParams.set(filterKey, filterValue as string);
          }
        }
      });
    }

    currentUrl.searchParams.set('start', '1');
    currentUrl.searchParams.set('take', '25');
    redirectWithoutRefresh(currentUrl?.search?.toString());
  }, []);

  const saveAISearchToLocalStorage = (aiSearchInputValues: string) => {
    const aiLeadSearchHistory = JSON.parse(
      StorageUtil.getItem('aiSearchHistory') || '{}',
    );
    if (!Array.isArray(aiLeadSearchHistory[userEmail])) {
      aiLeadSearchHistory[userEmail] = [];
    }
    aiLeadSearchHistory[userEmail] = aiLeadSearchHistory[userEmail]?.filter(
      (entry) =>
        entry.aiSearchInputValues?.toLowerCase() !==
        aiSearchInputValues?.toLowerCase(),
    );
    aiLeadSearchHistory[userEmail]?.unshift({ aiSearchInputValues });

    aiLeadSearchHistory[userEmail] = aiLeadSearchHistory[userEmail]?.slice(
      0,
      4,
    );
    StorageUtil.setItem('aiSearchHistory', JSON.stringify(aiLeadSearchHistory));
  };

  const populateRevealingLeadsStateAfterSearch = (profiles: any[]) => {
    if (!profiles?.length) {
      return;
    }
    const revealingSearchEmailLeads = profiles.filter(
      (lead) => lead.isRevealing && lead?.revealType === LeadRevealType.EMAIL,
    );
    const revealingSearchEmailPhoneLeads = profiles.filter(
      (lead) =>
        lead.isRevealing && lead?.revealType === LeadRevealType.EMAIL_PHONE,
    );
    const revealedLeads = profiles.filter((lead) => lead.isRevealed);
    // email
    const updateLoadingEmailLeads = [];
    updateLoadingEmailLeads.push(
      ...revealingSearchEmailLeads.map((lead) => [lead.id, true]),
    );
    updateLoadingEmailLeads.push(
      ...revealedLeads.map((lead) => [lead.id, false]),
    );
    setEmailRevealingLeads(() => ({
      ...Object.fromEntries(updateLoadingEmailLeads),
    }));

    // email phone
    const updateLoadingEmailPhoneLeads = [];
    updateLoadingEmailPhoneLeads.push(
      ...revealingSearchEmailPhoneLeads.map((lead) => [lead.id, true]),
    );
    updateLoadingEmailPhoneLeads.push(
      ...revealedLeads.map((lead) => [lead.id, false]),
    );
    setEmailPhoneRevealingLeads(() => ({
      ...Object.fromEntries(updateLoadingEmailPhoneLeads),
    }));
  };

  const handleFetchLeads = async (data: any, key: string) => {
    try {
      setIsLoading(true);
      let res: any = {};
      if (key === LeadFinderTabKey.PEOPLE_TAB) {
        if (aiSeachPopupSubmitted && aiSearchInputValue) {
          if (!isAiSearchInProgress) {
            dispatch(setIsAiSearchInProgress(true));
            res = await fetchAISearchResult({ aiText: aiSearchInputValue });
            dispatch(setIsAiSearchInProgress(false));
            if (res?.payload?.filterSource?.toLowerCase() === 'system') {
              toaster.info(
                "We couldn't find an exact match. Try modifying your search",
                {
                  delay: 4000,
                  theme: Theme.New,
                  showCloseIcon: true,
                },
              );
            }
            saveAISearchToLocalStorage(aiSearchInputValue);
            dispatch(setAiSearchPopupInputValue(''));
          }
        } else {
          res = await getLeads(data);
          populateRevealingLeadsStateAfterSearch(res?.payload?.profiles);
        }
      } else {
        res = await getSavedLeads(data);
      }
      if (res?.payload) {
        if (aiSeachPopupSubmitted) {
          updateUrlWithAiSearchParams(res?.payload?.filters || {});
        }

        setLeadsData(res.payload);
      }
    } catch (e: any) {
      if (e?.type === ExceptionTypes.RateLimit) {
        if (!leadsData?.profiles?.length || !leadsData?.pagination?.total) {
          setLeadsData({
            pagination: {
              start: 0,
              take: 0,
              total: 0,
            },
            profiles: [],
          });
        }
        return;
      }
      if (e?.isHandled) {
        setLeadsData({
          pagination: {
            start: 0,
            take: 0,
            total: 0,
          },
          profiles: [],
        });
        return;
      }
      toaster.error(e?.message);
    } finally {
      if (aiSeachPopupSubmitted) {
        // ** reset ai search popup submitted executed every time
        dispatch(setAiSearchPopupSubmitted(false));
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchLeadsWithAiFilters = async () => {
      const payload = handleGenerateAPIParam(tabKey);
      if (payload?.start && payload?.take) {
        handleFetchLeads(null, tabKey);
      }
    };
    if (aiSeachPopupSubmitted && aiSearchInputValue) {
      fetchLeadsWithAiFilters();
    }
  }, [aiSeachPopupSubmitted, aiSearchInputValue, tabKey]);

  useEffect(() => {
    const fetchLeads = async () => {
      const payload = handleGenerateAPIParam(tabKey);
      if (payload?.start && payload?.take) {
        const { start, take, ...rest } = payload;
        if (
          Object.keys(rest || {})?.length &&
          tabKey === LeadFinderTabKey.PEOPLE_TAB
        ) {
          handleFetchLeads(payload, tabKey);
        }
      }
    };
    fetchLeads();
  }, [tabKey]);

  useEffect(() => {
    const fetchLeads = async () => {
      const payload: any = handleGeneratePayload();
      if (payload?.start && payload?.take) {
        handleFetchLeads(payload, tabKey);
      }
    };
    if (tabKey === LeadFinderTabKey.SAVED_TAB) {
      fetchLeads();
    }
  }, [savedApiParam]);

  const searchLeads = (payload?: any, performSearch = true) => {
    if (payload) {
      if (tabKey === LeadFinderTabKey.PEOPLE_TAB) {
        setApiParam(payload);
      } else {
        setSavedApiParam(payload);
      }
      if (performSearch) {
        handleFetchLeads(payload, tabKey);
      }
      return;
    }
    const updatedPayload = handleGenerateAPIParam(tabKey);
    if (performSearch) {
      handleFetchLeads(updatedPayload, tabKey);
    }
  };

  const handlePagination = ({ start, take }) => {
    if (start && take) {
      queryParams.set('start', start);
      queryParams.set('take', take);
      history.push({
        search: queryParams.toString(),
        hash: getLeadsActiveTab(location),
      });
      const updatedPayload = handleGenerateAPIParam(tabKey);
      if (tabKey === LeadFinderTabKey.PEOPLE_TAB) {
        handleFetchLeads(updatedPayload, tabKey);
      }
    }
  };

  useEffect(() => {
    if (location?.search) {
      handleGenerateAPIParam(tabKey);
      setUrlState({
        payload: handleGeneratePayload(),
        url: location?.pathname + location?.search,
      });
    } else {
      setLeadsData({
        profiles: [],
        pagination: null,
      });
    }
  }, [location.search]);

  useEffect(
    () => () => {
      if (!urlState || window.location?.pathname === '/leads') {
        return; // If urlState is not set, do nothing
      }

      if (tabKey !== LeadFinderTabKey.PEOPLE_TAB) {
        return;
      }

      const { payload } = urlState;
      const meaningfulParams = Object.keys(payload)?.filter(
        (key) => key !== 'start' && key !== 'take',
      );

      if (meaningfulParams.length > 0 && userEmail) {
        const storedUrlsKey = 'storedUrls';
        const storedUrls = JSON.parse(StorageUtil.getItem(storedUrlsKey)) || {};

        if (!storedUrls[userEmail]) {
          storedUrls[userEmail] = [];
        }

        const timestamp = new Date().toISOString();

        const existingUrlIndex = storedUrls[userEmail].findIndex(
          (urlData) =>
            urlData?.url?.toLowerCase() === urlState?.url?.toLowerCase(),
        );

        const maxUrls = 4;

        if (existingUrlIndex !== -1) {
          const updatedEntry = {
            url: urlState?.url,
            timestamp,
            payload,
          };

          storedUrls[userEmail].unshift(
            storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
          );
          storedUrls[userEmail][0] = updatedEntry;

          storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
        } else {
          const urlData = {
            url: urlState?.url,
            timestamp,
            payload,
          };

          storedUrls[userEmail].unshift(urlData);

          storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
        }

        StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
      }
    },
    [urlState],
  );
  return {
    isLoading,
    leadsData,
    apiParam,
    savedApiParam,
    handlePagination,
    handleRecentSearch,
    userEmail,
    handleGeneratePayload,
    handleAppendLead,
    setLeadsData,
    handleRouteChange,
    searchLeads,
    populateRevealingLeadsStateAfterSearch,
  };
};
